import React from "react";
import FormInput from "../../../Components/Forms/FormInput/FormInput";
import { FormattedMessage, useIntl } from "react-intl";
import { createFormRequestHook, useFormState } from "../../../Components/Forms";
import {
  SideFormContent,
  SideFormFooter,
  SideForm
} from "../../../Components/Forms/SideMenuForm";
import { spaceBusinessAreaSchema } from "../../../config/schema";
import { useBusinessArea } from "../../../Hooks/EntityHooks";
import LoaderSpinner from "../../../Components/Loader/LoaderSpinner/LoaderSpinner";
import { useToast } from "../../../Components/Toast/ToastProvider";
import {
  BaseSidebarContainer,
  BaseSidebarHeader,
  useSidebarSpacePost,
  BaseSidebarContent,
  useSidebarSpaceDelete
} from "../../../Components/Sidebar/SidebarV2Helper";
import Switch from "../../../Components/Switch/Switch";
import { handleError } from "../../../Helpers/MiscHelper";

const convertEntityToForm = ({ Name, Enabled }) => ({ Name, Enabled });

const useBusinessAreaRequest = createFormRequestHook({
  schema: spaceBusinessAreaSchema,
  convertEntityToForm
});

const BusinessAreaForm = (props) => {
  const { id } = props;
  const { loading, error, convertedForm } = useBusinessAreaRequest(props);
  const businessArea = useBusinessArea(id);

  if (loading || error)
    return (
      <BaseSidebarContainer>
        <BaseSidebarHeader>
          <FormattedMessage id="BUSINESS_AREA" />
        </BaseSidebarHeader>
        <BaseSidebarContent>
          <div className="d-flex h-100 w-100 justify-content-center align-items-center">
            <LoaderSpinner center size="sm" className="text-secondary" />
          </div>
        </BaseSidebarContent>
      </BaseSidebarContainer>
    );

  return (
    <Form
      {...props}
      convertedForm={convertedForm}
      canEdit={businessArea?.CanEdit ?? true}
      canDelete={businessArea?.CanDelete ?? Boolean(id)}
    />
  );
};

const Form = ({ convertedForm, id, canEdit, canDelete, onSuccess, refetchRef }) => {
  const formState = useFormState(() => ({
    Name: "",
    Enabled: true,
    ...convertedForm
  }));
  const { form, handleChange } = formState;
  const { Name } = form;

  const createToast = useToast();
  const intl = useIntl();

  const [post, { loading }] = useSidebarSpacePost(
    id ? `businessarea/${id}` : `businessarea`,
    spaceBusinessAreaSchema,
    {
      onSuccess: () => {
        createToast({
          pos: "tm",
          type: "success",
          title: `${intl.formatMessage({ id: "BUSINESS_AREA" })} ${Name}`,
          description: intl.formatMessage({ id: "SUCCESS_SAVED" })
        });
        onSuccess && onSuccess();
      },
      refetchRef,
      onError: ({ error }) => {
        handleError(createToast, error);
      }
    }
  );

  const [remove, { loading: deleteLoading }] = useSidebarSpaceDelete(
    `businessarea/${id}`,
    spaceBusinessAreaSchema,
    {
      onSuccess: () => {
        createToast({
          pos: "tm",
          type: "success",
          title: `${intl.formatMessage({ id: "BUSINESS_AREA" })} ${Name}`,
          description: intl.formatMessage({ id: "SUCCESS_DELETED" })
        });
      },
      onError: ({ error }) => {
        handleError(createToast, error);
      },
      refetchRef
    }
  );

  const handleSubmit = ({ Name, Enabled }) => {
    post({ Name, Enabled });
  };

  const validate = ({ Name }, addError) => {
    if (!Name) addError("Name");
  };

  const handleCheckChanges = ({ target: { name, checked } }) => {
    handleChange({ [name]: checked });
  };

  return (
    <BaseSidebarContainer>
      <BaseSidebarHeader>
        <FormattedMessage id="BUSINESS_AREA" />
      </BaseSidebarHeader>
      <BaseSidebarContent>
        <SideForm
          canEdit={canEdit}
          formState={formState}
          onSubmit={handleSubmit}
          validate={validate}
        >
          <SideFormContent className="px-4 py-3">
            <FormInput name="Name" textId="NAME" className="mb-3" />
            <FormInput
              name="Enabled"
              onChange={handleCheckChanges}
              textId="ACTIVE"
              inputType={Switch}
              className="mb-3"
            />
          </SideFormContent>
          <SideFormFooter
            canDelete={canDelete}
            deleteLoading={deleteLoading}
            handleDelete={remove}
            isLoading={loading}
          />
        </SideForm>
      </BaseSidebarContent>
    </BaseSidebarContainer>
  );
};

export default BusinessAreaForm;
